<template>
    <div class="find-a-doctor">
        <info :query="filters.query" :count="pageInfo.total_results" v-if="hasFilters" type="providers" />
        <div class="find-a-doctor__container">
            <div class="container">
                <div class="find-a-doctor__layout">
                    <div class="find-a-doctor__filters">
                        <filters :class="{ '-is-disabled': ! state.filters }" :active="state.filters" @close="closeFilter" @search="handleFilterSearch" />
                    </div>
                    <div class="find-a-doctor__content">
                        <div class="find-a-doctor__header">
                            <div class="find-a-doctor__mobile-wrap">
                                <div class="find-a-doctor__active-filters">
                                    <active-filters label="Search" :filters="searchFilters" />
                                    <active-filters label="Filters" :filters="selectedFilters" />
                                </div>
                                <div class="find-a-doctor__button">
                                    <button class="button find-a-doctor__filter-toggle" @click="toggleFilter">Add Filters</button>
                                </div>
                            </div>
                            <transition-group name="list" class="find-a-doctor__results" tag="div" v-if="results.length">
                                <div class="find-a-doctor__results-item" v-for="result in results" :key="result.data.id">
                                    <personnel :data="result.data" ref="result" tabindex="0" :request-id="requestId" />
                                </div>
                            </transition-group>
                            <div class="find-a-doctor__results" v-else>
                                <div class="find-a-doctor__results-item" v-if="! results.length && ! state.loading">
                                    <h2 class="find-a-doctor__no-results">No Results</h2>
                                </div>
                                <div class="find-a-doctor__results-item -loading" v-else>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle fill="#818181" cx="6" cy="50" r="6"><animate attributeName="opacity" dur="1s" values="0;1;0" repeatCount="indefinite" begin=".1"/></circle><circle fill="#818181" cx="26" cy="50" r="6"><animate attributeName="opacity" dur="1s" values="0;1;0" repeatCount="indefinite" begin=".2"/></circle><circle fill="#818181" cx="46" cy="50" r="6"><animate attributeName="opacity" dur="1s" values="0;1;0" repeatCount="indefinite" begin=".3"/></circle></svg>
                                </div>
                            </div>
                            <div class="find-a-doctor__pagination">
                                <pagination
                                    :total-pages="pageInfo.total_pages"
                                    :current-page="pageInfo.current"
                                    :page-size="pageInfo.size"
                                    :max-links="6"
                                    v-if="Object.keys(pageInfo).length && results.length"
                                    @pageChange="handlePageChange"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { $extend, $serialize } from 'core/types';
import { _win } from 'core/variables';
import Tweezer from 'tweezer.js'
import debounce from 'lodash.debounce';
import $location from 'wee-location';
import qs from 'query-string';
import $ from 'wee-dom';
import Overlay from '../../components/overlay';
import Info from './info/index.vue';
import Filters from './filters/index.vue';
import ActiveFilters from './active-filters/index.vue';
import client from '../../scripts/swiftypeClient';
import Personnel from '../search/types/personnel.vue';
import Pagination from './pagination/index.vue';
import { baseFilters } from '../find-a-doctor';

const blacklist = [
    'Select a Specialty',
    'Select a Service',
    '',
    'Both',
    'All',
];

const buildFilters = rawFilters => {
    const filters = {
        all: [{
            type: 'personnel',
        }],
        any: [],
    };

    if (rawFilters.specialty && ! blacklist.includes(rawFilters.specialty)) {
        filters.any.push({ specialties: rawFilters.specialty });
    }

    if (rawFilters.service && ! blacklist.includes(rawFilters.service)) {
        filters.all.push({ services: rawFilters.service });
    }

    if (rawFilters.condition) {
        filters.any.push({ conditions: rawFilters.condition });
    }

    if (rawFilters.gender && ! blacklist.includes(rawFilters.gender)) {
        filters.all.push({ gender: rawFilters.gender });
    }

    if (rawFilters.location && ! blacklist.includes(rawFilters.location)) {
        filters.all.push({ locations: rawFilters.location });
    }

    if (rawFilters.language && ! blacklist.includes(rawFilters.language)) {
        filters.all.push({ languages: rawFilters.language });
    }

    if (rawFilters.primaryCarePhysician) {
        filters.all.push({ primary_care_physician: 'true' });
    }

    if (rawFilters.treatsChildren) {
        filters.all.push({ treats_children: 'true' });
    }

    // Remove MCI Researchers
    if (rawFilters.researcher) {
        filters.all.push({ researcher: 'true' });
        // filters.any.push({ researcher_at_mci: 'true' });
    } else {
        filters.all.push({ researcher_at_mci: 'false' });
    }

    return filters;
};

export default {
    components: {
        Filters,
        Info,
        ActiveFilters,
        Personnel,
        Pagination,
    },
    mounted() {
        this.overlay = new Overlay({
            closeOnClick: true,
            callback: () => this.closeFilter(),
        });

        const query = qs.parse($location.uri().search);

        if (query && Object.keys(query).length) {
            for (const filter in query) {
                if (this.filters[filter] !== undefined) {
                    // TODO: Probably set all filters at once
                    this.setFilter({
                        filter,
                        value: query[filter],
                    });
                }
            }

            if (query.page) {
                this.page = Number(query.page);
            }
        }

        // If no filters, initiate search.
        if (! this.hasFilters) {
            this.search();
        }
    },
    methods: {
        ...mapActions(['setFilter']),
        handleFilterSearch() {
            this.closeFilter();
            this.search();
        },
        search(opts = {}) {
            const query = this.filters.name;
            const filters = buildFilters(this.filters);
            const options = $extend({
                search_fields: {
                    title: {},
                },
                filters,
                page: {
                    size: 10,
                    current: this.page,
                },
            }, opts);

            this.state.loading = true;

            if (this.shouldSort()) {
                options.sort = {
                    last_name: 'asc',
                };
            }

            client.search(query, options).then((response) => {
                this.results = response.results.map(item => {
                    const data = {};

                    for (const key in item.data) {
                        data[key] = item.data[key].raw;

                        // Convert string booleans to boolean booleans
                        if (data[key] === 'true' || data[key] === 'false') {
                            data[key] = data[key] === 'true';
                        }
                    }

                    data.alt = `A picture of ${data.title}`

                    if (! data.image) {
                        // TODO: CDN URL?  How do?
                        data.image = '/assets/images/global/personnel-default.jpg';
                        data.alt = 'No portrait';
                    }

                    return { data };
                });

                this.pageInfo = response.info.meta.page;
                this.state.loading = false;
                this.requestId = response.info.meta.request_id;
            });
        },
        toggleFilter() {
            this.overlay.toggle();

            this.state.filters = ! this.state.filters;
        },
        closeFilter() {
            this.overlay.close();

            this.state.filters = false;
        },
        handlePageChange(page) {
            this.page = page;

            this.processQuery();
            this.search();

            new Tweezer({
                start: $(_win).scrollTop(),
                end: 150,
                duration: 500,
            }).on('tick', value => {
                _win.requestAnimationFrame(() => _win.scrollTo(0, value));
            }).on('done', () => {
                if (this.$refs.result && this.$refs.result.length) {
                    this.$refs.result[0].$el.focus();
                }
            }).begin();
        },
        processQuery() {
            const query = qs.parse($location.uri().search);
            const filters = {};

            for (const filter in this.filters) {
                if (
                    ! blacklist.includes(this.filters[filter])
                    && this.filters[filter] !== baseFilters[filter]
                ) {
                    filters[filter] = this.filters[filter];
                } else {
                    delete filters[filter];
                    delete query[filter];
                }
            }

            // Set page
            filters.page = this.page;

            const queryString = qs.stringify($extend(query, filters));
            const path = `${$location.uri().path}?${queryString}`;

            history.pushState({ path }, '', path);
        },
        shouldSort() {
            let query = $location.uri().query;

            return ! query.name;
        },
    },
    watch: {
        filters: {
            deep: true,
            handler: debounce(function () {
                // Reset page
                this.page = 1;

                this.processQuery();
                this.search();
            }, 500),
        },
    },
    computed: {
        ...mapGetters(['filters']),
        hasFilters() {
            let query = $location.uri().query;

            delete query.page;

            // If query parameter is empty string, delete it to initiate search
            if (query.query === '') {
                delete query.query;
            }

            return Object.keys(query).length > 0;
        },
        searchFilters() {
            return [
                {
                    filter: 'name',
                    label: 'Name',
                    clearable: true,
                    value: this.filters.name,
                },
                {
                    filter: 'query',
                    label: false,
                    valueOnly: true,
                    clearable: true,
                    value: this.filters.query,
                },
                {
                    filter: 'specialty',
                    label: 'Specialty',
                    clearable: true,
                    value: this.filters.specialty !== 'Select a Specialty' ? this.filters.specialty : '',
                },
                {
                    filter: 'service',
                    label: 'Service',
                    clearable: true,
                    value: this.filters.service !== 'Select a Service' ? this.filters.service : '',
                },
                {
                    filter: 'condition',
                    label: 'Condition',
                    clearable: true,
                    value: this.filters.condition,
                },
            ];
        },
        selectedFilters() {
            return [
                {
                    filter: 'gender',
                    label: 'Gender',
                    clearable: this.filters.gender !== 'Both',
                    value: this.filters.gender,
                },
                {
                    filter: 'location',
                    label: 'Location',
                    clearable: this.filters.location !== 'All',
                    value: this.filters.location,
                },
                {
                    filter: 'language',
                    label: 'Language',
                    clearable: this.filters.language !== 'All',
                    value: this.filters.language,
                },
                {
                    filter: 'primaryCarePhysician',
                    label: 'Primary Care Physician',
                    clearable: true,
                    labelOnly: true,
                    value: this.filters.primaryCarePhysician,
                },
                {
                    filter: 'treatsChildren',
                    label: 'Treats Children',
                    clearable: true,
                    labelOnly: true,
                    value: this.filters.treatsChildren,
                },
                {
                    filter: 'researcher',
                    label: 'Researcher',
                    clearable: true,
                    labelOnly: true,
                    value: this.filters.researcher,
                },
            ];
        }
    },
    data: () => ({
        overlay: null,
        results: [],
        pageInfo: {},
        requestId: null,
        page: 1,
        state: {
            filters: false,
            loading: true,
        },
    })
};
</script>
